import ConversationPanelLeft from '@/components/content/ConversationPanelLeft';
import UsageTrendChart from '@/components/UsageTrendChart';
import ContentToolbar from '@/coral/components/Content/ContentToolbar';
import { apiService, ChannelUsage, MonthlyUsageStats, UsageCategory, UsageTotals } from '@/api/apiService';
import {
    Body1,
    Body1Strong,
    Card,
    Select,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Title2,
} from '@fluentui/react-components';
import React, { useEffect, useState } from 'react';
import Content from '../coral/components/Content/Content';
import CoralShellColumn from '../coral/components/Layout/CoralShellColumn';
import CoralShellRow from '../coral/components/Layout/CoralShellRow';
import '../styles/PlanPage.css';
import '../styles/HomeInput.css';

type LoadState =
    | { kind: 'loading' }
    | { kind: 'forbidden' }
    | { kind: 'error' }
    | { kind: 'ready'; stats: MonthlyUsageStats[] };

const cardStyle: React.CSSProperties = {
    flex: '1',
    padding: '16px',
    backgroundColor: 'var(--colorNeutralBackground3)',
    border: '1px solid var(--colorNeutralStroke2)',
    borderRadius: '8px',
    boxShadow: 'none',
};

const mutedStyle: React.CSSProperties = { color: 'var(--colorNeutralForeground3)' };

const formatMonth = (month: string) =>
    new Date(`${month}-01T00:00:00Z`).toLocaleDateString('en-GB', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
    });

const StatCard: React.FC<{ label: string; value: number }> = ({ label, value }) => (
    <Card style={cardStyle}>
        <Body1 style={mutedStyle}>{label}</Body1>
        <Title2>{value}</Title2>
    </Card>
);

const CategoryTable: React.FC<{ label: string; rows: UsageCategory[] }> = ({ label, rows }) => (
    <Table size="small" aria-label={label} style={{ marginTop: '12px' }}>
        <TableHeader>
            <TableRow>
                <TableHeaderCell><Body1Strong>{label}</Body1Strong></TableHeaderCell>
                <TableHeaderCell><Body1Strong>Replies</Body1Strong></TableHeaderCell>
            </TableRow>
        </TableHeader>
        <TableBody>
            {rows.map((row) => (
                <TableRow key={row.category}>
                    <TableCell>{row.category}</TableCell>
                    <TableCell>{row.replies}</TableCell>
                </TableRow>
            ))}
        </TableBody>
    </Table>
);

const CategoriesSection: React.FC<{ period: string }> = ({ period }) => {
    const [rows, setRows] = useState<UsageCategory[] | null | 'error'>(null);

    useEffect(() => {
        let active = true;
        apiService
            .getUsageCategories(period)
            .then((result) => active && setRows(result))
            .catch(() => active && setRows('error'));
        return () => {
            active = false;
        };
    }, [period]);

    const topics = Array.isArray(rows) ? rows.filter((row) => row.group === 'topic') : [];
    const quality = Array.isArray(rows) ? rows.filter((row) => row.group === 'quality') : [];

    return (
        <div>
            <Body1Strong>What people ask about</Body1Strong>
            {rows === null && <Spinner size="tiny" style={{ marginTop: '12px' }} />}
            {rows === 'error' && (
                <Body1 as="p" style={mutedStyle}>Couldn't load categories.</Body1>
            )}
            {Array.isArray(rows) && rows.length === 0 && (
                <Body1 as="p" style={mutedStyle}>No replies in this period.</Body1>
            )}
            {topics.length > 0 && <CategoryTable label="Topic" rows={topics} />}
            {quality.length > 0 && <CategoryTable label="Clarifications & errors" rows={quality} />}
            {Array.isArray(rows) && rows.length > 0 && (
                <Body1 as="p" style={mutedStyle}>
                    Counts the chatbot's replies by which part of it answered.
                </Body1>
            )}
        </div>
    );
};

const CHANNEL_LABELS: Record<string, string> = { web: 'Web', cloudportal: 'Cloud Portal', other: 'Other' };

const ChannelsSection: React.FC<{ period: string }> = ({ period }) => {
    const [rows, setRows] = useState<ChannelUsage[] | null | 'error'>(null);

    useEffect(() => {
        let active = true;
        setRows(null);
        apiService
            .getUsageChannels(period)
            .then((result) => active && setRows(result))
            .catch(() => active && setRows('error'));
        return () => {
            active = false;
        };
    }, [period]);

    return (
        <div>
            <Body1Strong>By channel</Body1Strong>
            {rows === null && <Spinner size="tiny" style={{ marginTop: '12px' }} />}
            {rows === 'error' && <Body1 as="p" style={mutedStyle}>Couldn't load channels.</Body1>}
            {Array.isArray(rows) && rows.length === 0 && (
                <Body1 as="p" style={mutedStyle}>No questions in this period.</Body1>
            )}
            {Array.isArray(rows) && rows.length > 0 && (
                <Table size="small" aria-label="Usage by channel" style={{ marginTop: '12px' }}>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell>Channel</TableHeaderCell>
                            <TableHeaderCell>Users</TableHeaderCell>
                            <TableHeaderCell>Sessions</TableHeaderCell>
                            <TableHeaderCell>Queries</TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={row.channel}>
                                <TableCell>{CHANNEL_LABELS[row.channel] ?? row.channel}</TableCell>
                                <TableCell>{row.users}</TableCell>
                                <TableCell>{row.sessions}</TableCell>
                                <TableCell>{row.queries}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </div>
    );
};

const UsageBody: React.FC<{ state: LoadState }> = ({ state }) => {
    if (state.kind === 'loading') return <Spinner label="Loading usage..." />;
    if (state.kind === 'forbidden') {
        return <Body1 style={mutedStyle}>You don't have access to usage insights.</Body1>;
    }
    if (state.kind === 'error') {
        return <Body1 style={mutedStyle}>Couldn't load usage insights. Please try again later.</Body1>;
    }
    if (state.stats.length === 0) return <Body1 style={mutedStyle}>No usage recorded yet.</Body1>;
    return <UsageReport stats={state.stats} />;
};

const ALL_TIME = 'all';
const RECENT_PERIODS: [string, string][] = [
    ['last3', 'Last 3 months'],
    ['last6', 'Last 6 months'],
    ['last12', 'Last 12 months'],
];

// Users and sessions cannot be summed across months, so ranges are counted by the server.
const SummaryCards: React.FC<{ period: string; stats: MonthlyUsageStats[] }> = ({ period, stats }) => {
    const month = stats.find((row) => row.date === period);
    const [totals, setTotals] = useState<UsageTotals | null | 'error'>(null);

    useEffect(() => {
        if (month) return;
        let active = true;
        setTotals(null);
        apiService
            .getUsageSummary(period)
            .then((result) => active && setTotals(result))
            .catch(() => active && setTotals('error'));
        return () => {
            active = false;
        };
    }, [period, month]);

    const shown = month
        ? { users: month.unique_users, sessions: month.unique_sessions, queries: month.unique_messages }
        : totals;
    if (shown === null) return <Spinner size="tiny" style={{ marginTop: '12px' }} />;
    if (shown === 'error') return <Body1 as="p" style={mutedStyle}>Couldn't load totals.</Body1>;
    return (
        <div className="home-input-quick-tasks" style={{ marginTop: '12px' }}>
            <StatCard label="Users" value={shown.users} />
            <StatCard label="Sessions" value={shown.sessions} />
            <StatCard label="Queries" value={shown.queries} />
        </div>
    );
};

const UsageReport: React.FC<{ stats: MonthlyUsageStats[] }> = ({ stats }) => {
    const [period, setPeriod] = useState(stats[0].date);
    const lastTwelve = stats.slice(0, 12);

    return (
        <>
            <div>
                <Select
                    aria-label="Period"
                    value={period}
                    onChange={(_, data) => setPeriod(data.value)}
                    style={{ maxWidth: '220px' }}
                >
                    <option value={ALL_TIME}>All time</option>
                    {RECENT_PERIODS.map(([value, label]) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                    {stats.map((row) => (
                        <option key={row.date} value={row.date}>
                            {formatMonth(row.date)}
                        </option>
                    ))}
                </Select>
                <SummaryCards period={period} stats={stats} />
            </div>

            <ChannelsSection period={period} />

            <CategoriesSection period={period} />

            <div>
                <Body1Strong>Trend (last 12 months)</Body1Strong>
                <UsageTrendChart stats={lastTwelve} />
            </div>

            <div>
                <Body1Strong>By month (last 12 months)</Body1Strong>
                <Table size="small" aria-label="Usage by month" style={{ marginTop: '12px' }}>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell>Month</TableHeaderCell>
                            <TableHeaderCell>Users</TableHeaderCell>
                            <TableHeaderCell>Sessions</TableHeaderCell>
                            <TableHeaderCell>Queries</TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {lastTwelve.map((row) => (
                            <TableRow key={row.date}>
                                <TableCell>{formatMonth(row.date)}</TableCell>
                                <TableCell>{row.unique_users}</TableCell>
                                <TableCell>{row.unique_sessions}</TableCell>
                                <TableCell>{row.unique_messages}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Body1 style={mutedStyle}>
                Months are in UTC. Users and sessions count anyone who asked at least one question in the
                period, each once; queries counts every question asked. "Last N months" includes this
                month.
            </Body1>
        </>
    );
};

/**
 * Admin-only usage page, accessible via "/admin/usage". The backend enforces access.
 */
const AdminUsagePage: React.FC = () => {
    const [state, setState] = useState<LoadState>({ kind: 'loading' });

    useEffect(() => {
        let active = true;
        apiService
            .getUsageStats()
            .then((stats) => active && setState({ kind: 'ready', stats }))
            .catch((error) => active && setState({ kind: error?.status === 403 ? 'forbidden' : 'error' }));
        return () => {
            active = false;
        };
    }, []);

    return (
        <CoralShellColumn>
            <CoralShellRow>
                <ConversationPanelLeft />
                <Content>
                    <ContentToolbar panelTitle={"Public Cloud Intelligent Assistant"}></ContentToolbar>
                    <div className="home-input-container" style={{ overflowY: 'auto' }}>
                        <div className="home-input-content" style={{ justifyContent: 'flex-start' }}>
                            <div className="home-input-center-content">
                                <div className="home-input-title-wrapper">
                                    <Title2>Usage insights</Title2>
                                </div>
                                <UsageBody state={state} />
                            </div>
                        </div>
                    </div>
                </Content>
            </CoralShellRow>
        </CoralShellColumn>
    );
};

export default AdminUsagePage;
